import React from 'react';
import { UserRole } from '../../enums/userRole';
import { hasMinRole } from '../../lib/permissions';
import styles from './EditUserModal.module.css';
import { EditUserModalProps } from '../../types/types';
import Checkbox from '../../ui/Checkbox/Checkbox';
import { useUsersStore } from '../../store/usersStore';
import { toast } from '../../lib/toast';
import Button from '../../ui/Button/Button';
import SecureInput from '../../ui/SecureInput/SecureInput';
import { Mail, User, Lock } from 'lucide-react';
import { RegistrationSource } from '../../enums/registrationSource';


const EditUserModal: React.FC<EditUserModalProps> = ({
  editingUser,
  setEditingUser,
  editUserForm,
  setEditUserForm,
  currentUser
}) => {

  const { updateUser: updateUserInStore } = useUsersStore()


  const updateUser = async () => {
    if (!editingUser) return

    if (!editUserForm.first_name || !editUserForm.last_name || !editUserForm.email) {
      toast.error('Please fill in all required fields')
      return
    }
    if (editUserForm.password==='') {
      delete editUserForm.password
    }
    try {
      await updateUserInStore(editingUser.id, editUserForm)
      setEditingUser(null)
      toast.success('User updated successfully!')
    } catch (error: any) {
      toast.error(`Failed to update user: ${error.message}`)
    }
  }

  const cancelEdit = () => {
    setEditingUser(null)
    setEditUserForm({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      role: null,
      is_active: true
    })
  }

  const isIdpUser = editingUser?.registrationSource === RegistrationSource.Idp;

  if (!editingUser) return null;
  return (
    <div className={styles.editModal}>
      <div className={styles.modalOverlay} onClick={cancelEdit} />
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>Edit User</h3>
          <p>Update user information and permissions</p>
        </div>
        <div className={styles.editForm}>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <SecureInput
                fieldType="email"
                label="Email Address *"
                icon={<Mail size={16} />}
                value={editUserForm.email}
                onChange={(value) => setEditUserForm({ ...editUserForm, email: value })}
                placeholder="Email address"
                disabled={true}
                required
                // className={styles.formInput}
              />
            </div>
            <div className={styles.formField}>
              <SecureInput
                fieldType="name"
                fieldName="First name"
                label="First Name *"
                icon={<User size={16} />}
                value={editUserForm.first_name}
                onChange={(value) => setEditUserForm({ ...editUserForm, first_name: value })}
                placeholder="First name"
                required
                // className={styles.formInput}
              />
            </div>
            <div className={styles.formField}>
              <SecureInput
                fieldType="name"
                fieldName="Last name"
                label="Last Name *"
                icon={<User size={16} />}
                value={editUserForm.last_name}
                onChange={(value) => setEditUserForm({ ...editUserForm, last_name: value })}
                placeholder="Last name"
                required
                // className={styles.formInput}
              />
            </div>
            {!isIdpUser && (
              <div className={styles.formField}>
                <SecureInput
                  fieldType="password"
                  label="New Password (optional)"
                  icon={<Lock size={16} />}
                  value={editUserForm.password}
                  onChange={(value) => setEditUserForm({ ...editUserForm, password: value })}
                  placeholder="Leave blank to keep current password"
                  skipPasswordValidation={true}
                />
              </div>
            )}
            <div className={styles.formField}>
              <label className={styles.fieldLabel}>Role *</label>
              <select
                value={editUserForm.role ?? UserRole.Viewer}
                onChange={(e) => setEditUserForm({ ...editUserForm, role: Number(e.target.value) })}
                className={styles.formInput}
              >
                <option value={UserRole.Viewer}>Viewer</option>
                <option value={UserRole.ToolManager}>Tool Manager</option>
                {hasMinRole(currentUser?.role?.id, UserRole.Admin) && (
                  <option value={UserRole.WorkzoneManager}>Workzone Manager</option>
                )}
                {hasMinRole(currentUser?.role?.id, UserRole.Admin) && (
                  <option value={UserRole.PromptEditor}>Prompt Editor</option>
                )}
                {hasMinRole(currentUser?.role?.id, UserRole.Admin) && (
                  <option value={UserRole.Admin}>Admin</option>
                )}
                {currentUser?.role?.id === UserRole.SuperAdmin && (
                  <option value={UserRole.SuperAdmin}>Super Admin</option>
                )}
              </select>
            </div>
            <div className={styles.formField}>
              <label className={styles.fieldLabel}>Status</label>
              <label className={styles.checkboxLabel}>
                <Checkbox
                  checked={editUserForm.is_active}
                  onChange={(e) => setEditUserForm({ ...editUserForm, is_active: e })}
                />
                Active User
              </label>
            </div>
          </div>
          <div className={styles.modalActions}>
            <Button variant="cancel" size="lg" onClick={cancelEdit}>
              Cancel
            </Button>
            <Button variant="primary" size="lg" onClick={updateUser}>
              Update User
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
